/**
 * The application shell: header, navigation, and the surface.
 *
 * The navigation is built from the role's grants, exactly as the server builds
 * it. There is no menu in this codebase. A menu written here would be a second
 * statement of who may see what, and a role granted a new section would not see
 * it until somebody remembered to add a link.
 */
import { useState } from 'react'
import type { ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'

import { api, IS_STATIC } from '../api/client'
import { useSession } from './session'
import { StaticBanner } from '../components/StaticBanner'
import { MOUNT, absolutePath, appPath, platformPath } from './paths'

export function Shell({ children }: { children: ReactNode }) {
  const { boot, t, dir, nm } = useSession()
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  const here = platformPath(location.pathname)
  const p = boot.principal

  const signOut = async () => {
    await api.signOut()
    window.location.assign(`${MOUNT}/`)
  }

  const switchRole = async (roleCode: string) => {
    if (roleCode === p.acting_role) return
    const result = await api.switchRole(roleCode)
    // A role switch opens a new session with different grants, a different
    // navigation and possibly a different palette, so the shell is rebuilt from
    // the server rather than patched in place.
    window.location.assign(absolutePath(result.landing))
  }

  const switchLocale = async (code: string) => {
    await api.setLocale(code)
    window.location.reload()
  }

  return (
    <>
      {IS_STATIC && <StaticBanner />}
      <header className="hdr">
        <button className="hdr-btn menu-toggle" type="button"
                onClick={() => setMenuOpen((v) => !v)}
                aria-label={t('nav.open_menu', 'Open menu')}>☰</button>

        <Link className="hdr-brand" to={appPath(boot.landing)}
              style={{ color: 'inherit', textDecoration: 'none' }}>
          <span className="plate">{p.logo_initials || 'FP'}</span>
          <span>{boot.app_name}<small>{t('app.tagline',
            'National MSME growth orchestration')}</small></span>
        </Link>

        <div className="hdr-spacer" />
        <span className="hdr-env">{IS_STATIC ? 'recording · react'
          : `${boot.environment} · react`}</span>

        {boot.allow_locale_switch && boot.locales.length > 1 && (
          <nav className="lang-switch" aria-label={t('nav.language', 'Language')}>
            {boot.locales.map((l) => (
              <button key={l.locale_code} type="button" lang={l.locale_code}
                      className={l.locale_code === boot.locale.locale_code ? 'on' : ''}
                      aria-current={l.locale_code === boot.locale.locale_code}
                      onClick={() => void switchLocale(l.locale_code)}>
                {l.name_native}
              </button>
            ))}
          </nav>
        )}

        <div className="hdr-user">
          <div className="who">
            <b>{p.display_name}</b>
            <span>
              {dir === 'rtl' && p.role_name_ar ? p.role_name_ar : p.role_name}
              {p.partner_short ? ` · ${p.partner_short}` : ''}
            </span>
          </div>
          <span className="avatar" style={{ background: boot.palette.accent,
                                            color: boot.palette.on_accent }}>
            {(p.display_name || '?').charAt(0).toUpperCase()}
          </span>
          {p.roles_held.length > 1 && (
            <select className="hdr-btn" value={p.acting_role}
                    aria-label={t('nav.switch_role', 'Act as another role')}
                    onChange={(e) => void switchRole(e.target.value)}>
              {p.roles_held.map((r) => (
                <option value={r.role_code} key={r.role_code}>
                  {nm(r as unknown as Record<string, unknown>)}
                </option>
              ))}
            </select>
          )}
          <button className="hdr-btn" type="button" onClick={() => void signOut()}>
            {t('nav.sign_out', 'Sign out')}
          </button>
        </div>
      </header>

      <div className="shell">
        <nav className={`nav ${menuOpen ? 'on' : ''}`} aria-label="Sections"
             onClick={() => setMenuOpen(false)}>
          <Link to={appPath('/app/inbox')} className={here === '/app/inbox' ? 'on' : ''}>
            <span className="dot" /> {t('nav.inbox', 'Task inbox')}
          </Link>
          {boot.menu.map((group) => (
            <div key={group.code}>
              <div className="nav-cat">
                {dir === 'rtl' && group.name_ar ? group.name_ar : group.name}
              </div>
              {group.links.map((item) => (
                <Link key={item.key} to={appPath(item.route)}
                      className={here === item.route ? 'on' : ''}>
                  <span className="dot" />
                  {dir === 'rtl' && item.name_ar ? item.name_ar : item.name}
                  {item.build_state === 'placeholder' && (
                    <span className="nav-ph"
                          title={`Declared, not built. Scheduled for ${item.release_phase}.`}>
                      {item.release_phase}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          ))}
        </nav>

        <main className="main">{children}</main>
      </div>
    </>
  )
}
